import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Thermometer, Users, HeartHandshake, Sparkles, Waves, Star, CalendarDays, ArrowRight } from "lucide-react";

const STARS = [1, 2, 3, 4, 5];

const BOOK_URL = "https://portal.pivot1st.co.uk/book/2/class";

export default function Hero() {
  return (
    <section id="top" className="relative overflow-hidden pt-16 pb-24 md:pt-24 md:pb-32" data-testid="hero-section">
      <div className="blob absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#0EA5E9]/10" />
      <div className="blob absolute top-1/2 -left-40 w-[420px] h-[420px] rounded-full bg-[#FF7A1A]/10" />

      <div className="container mx-auto px-6 md:px-12 relative">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <span className="inline-flex items-center gap-2 text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">
                <Waves size={16} /> STA Swim Star School • Since 2014
              </span>
              <Link
                to="/term-dates"
                className="group inline-flex items-center gap-2 rounded-full border border-[#0A2540]/15 bg-white/70 backdrop-blur px-4 py-2 text-sm font-bold text-[#0A2540] shadow-sm hover:bg-[#0A2540] hover:text-white hover:-translate-y-0.5 transition-all duration-300"
                data-testid="hero-term-dates-link"
              >
                <CalendarDays size={15} className="text-[#FF7A1A] group-hover:text-white transition-colors" />
                Term dates
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter text-[#0F172A] leading-[1.05]">
              Confident swimmers{" "}
              <span className="text-[#0EA5E9]">for life</span>.
            </h1>
            <p className="mt-5 font-heading text-xl md:text-2xl font-semibold text-[#FF7A1A]">
              Built on trust, safety and joyful learning.
            </p>
            <p className="mt-5 text-lg text-[#334155] leading-relaxed max-w-xl">
              Safe, supportive swimming lessons that help everyone grow in
              confidence from day one — for babies, children and adults in
              Barton-le-Clay.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/classes"
                className="bg-[#FF7A1A] text-white font-bold px-8 py-4 rounded-full shadow-[0_8px_20px_-6px_rgba(255,122,26,0.5)] hover:shadow-[0_12px_24px_-6px_rgba(255,122,26,0.6)] hover:-translate-y-1 transition-all duration-300"
                data-testid="hero-primary-cta"
              >
                Find the right class
              </Link>
              <a
                href={BOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#0EA5E9]/10 text-[#0EA5E9] font-bold px-8 py-4 rounded-full hover:bg-[#0EA5E9] hover:text-white transition-all duration-300"
                data-testid="hero-secondary-cta"
              >
                Book now
              </a>
            </div>

            <div className="mt-12 flex flex-wrap gap-8">
              <Stat icon={<Thermometer size={18} />} value="31–33°C" label="Warm, cosy water" />
              <Stat icon={<Users size={18} />} value="Small classes" label="Real 1:1 attention" />
              <Stat icon={<HeartHandshake size={18} />} value="Family bonding" label="Two adults in the pool" />
              <Stat icon={<Sparkles size={18} />} value="Private pool" label="Calm & exclusive" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="relative"
          >
            <div className="rounded-[2.5rem] overflow-hidden shadow-2xl shadow-[#0A2540]/20 rotate-2 hover:rotate-0 transition-transform duration-500">
              <img
                src={`${process.env.PUBLIC_URL}/hero-home.jpg`}
                alt="A happy child swimming towards the camera during a lesson"
                className="w-full h-[420px] md:h-[560px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-3xl shadow-xl px-6 py-5 hidden sm:block">
              <span className="flex items-center gap-0.5 text-[#FBBC04] mb-1.5">
                {STARS.map((n) => (
                  <Star key={n} size={16} fill="#FBBC04" strokeWidth={0} />
                ))}
              </span>
              <p className="font-heading font-extrabold text-lg text-[#0A2540] leading-none">Loved by families</p>
              <p className="mt-1 text-sm text-[#64748B]">In Barton-le-Clay &amp; beyond</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Stat({ icon, value, label }) {
  return (
    <div className="flex items-center gap-3">
      <span className="grid place-items-center w-10 h-10 rounded-xl bg-[#0EA5E9]/10 text-[#0EA5E9]">{icon}</span>
      <div>
        <p className="font-heading font-extrabold text-xl text-[#0F172A] leading-none">{value}</p>
        <p className="text-sm text-[#64748B]">{label}</p>
      </div>
    </div>
  );
}
